import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { alpha, useTheme } from '@mui/material/styles';
import NRSendIcon from '../../../NRIcons/NRSendIcon';

const ChatInput = React.memo(function ChatInput({ inputRef, onSend, onKeyDown, isLoading, isLoadingSession, sessionId }) {
  const theme = useTheme();
  const [hasContent, setHasContent] = useState(false);
  
  // Helper to check content
  const checkContent = React.useCallback((value) => {
    const trimmed = (value || '').trim();
    setHasContent(trimmed.length > 0);
  }, []);

  const isSendDisabled = !hasContent || isLoading || isLoadingSession || !sessionId;

  return (
    <Box
      sx={{
        borderTop: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(2, 3),
        backgroundColor: theme.palette.background.paper
      }}
    >
      <TextField
        inputRef={inputRef}
        fullWidth
        multiline
        maxRows={4}
        placeholder="How can RiseAI help?"
        onKeyDown={onKeyDown}
        onKeyUp={(e) => {
          // Update hasContent state only on keyup (less frequent than onChange)
          checkContent(e.target.value);
        }}
        onFocus={(e) => {
          // Check content on focus (also fires when value is set programmatically and focused)
          checkContent(e.target.value);
        }}
        onInput={(e) => {
          // Also check on input for immediate feedback (but this is still less frequent than controlled onChange)
          checkContent(e.target.value);
        }}
        disabled={isLoading || isLoadingSession || !sessionId}
        variant="outlined"
        size="small"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => onSend()}
                disabled={isSendDisabled}
                sx={{
                  backgroundColor: isSendDisabled ? theme.palette.action.disabledBackground : theme.palette.secondary.main,
                  '&.Mui-disabled': {
                    backgroundColor: theme.palette.action.disabledBackground
                  },
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.secondary.main, 0.5)
                  }
                }}
              >
                <NRSendIcon color={isSendDisabled ? theme.palette.text.disabled : theme.palette.secondary.contrastText} />
              </IconButton>
            </InputAdornment>
          )
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: theme.palette.background.default,
            padding: theme.spacing(2),
            borderColor: theme.palette.text.border,
            borderRadius: theme.shape.borderRadius * 0.5,
            '&.Mui-focused fieldset': {
              borderColor: theme.palette.text.border
            }
          }
        }}
      />
      <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mt: 1, display: 'block', textAlign: 'center' }}>
        Check AI content for accuracy.
      </Typography>
    </Box>
  );
});

export default ChatInput;
